/**
 * Utility functions for handling organization files with presigned URLs
 */

/**
 * Extract S3 file key from a full S3 URL
 * @param {string} fileUrl - Full S3 URL (e.g., https://bucket.s3.region.amazonaws.com/organizations/uuid/drones/file.jpg)
 * @returns {string|null} - S3 key (e.g., organizations/uuid/drones/file.jpg) or null if invalid
 */
export const extractFileKeyFromUrl = (fileUrl) => {
  if (!fileUrl || typeof fileUrl !== 'string') return null;

  try {
    // Pattern 1: https://bucket-name.s3.region.amazonaws.com/key
    const s3Pattern1 = /https?:\/\/[^/]+\.s3\.[^/]+\.amazonaws\.com\/(.+)/;
    const match1 = fileUrl.match(s3Pattern1);
    if (match1) return match1[1];

    // Pattern 2: https://s3.region.amazonaws.com/bucket-name/key
    const s3Pattern2 = /https?:\/\/s3\.[^/]+\.amazonaws\.com\/[^/]+\/(.+)/;
    const match2 = fileUrl.match(s3Pattern2);
    if (match2) return match2[1];

    // If URL doesn't match S3 patterns, it might already be a key
    // or it's an external URL (return null)
    if (fileUrl.startsWith('organizations/')) {
      return fileUrl;
    }

    return null;
  } catch (error) {
    return null;
  }
};

/**
 * Fetch presigned URL for viewing an organization file
 * @param {string} fileUrl - S3 URL or file key
 * @param {string} organizationUuid - Organization UUID
 * @param {function} getAccessTokenSilently - Auth0 token function
 * @returns {Promise<string|null>} - Presigned URL or null if failed
 */
export const getPresignedViewUrl = async (fileUrl, organizationUuid, getAccessTokenSilently) => {
  if (!fileUrl) return null;

  try {
    // Extract file key from full URL
    const fileKey = extractFileKeyFromUrl(fileUrl);
    if (!fileKey) {
      return fileUrl; // Return original URL as fallback
    }

    // Get access token
    const token = await getAccessTokenSilently();

    // Fetch presigned URL from backend
    const response = await fetch(
      `${process.env.REACT_APP_API_URL}/api/organization/${organizationUuid}/files/view-url?fileKey=${encodeURIComponent(fileKey)}`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      return null;
    }

    const data = await response.json();
    return data.url;
  } catch (error) {
    return null;
  }
};

/**
 * Batch fetch presigned URLs for multiple files
 * More efficient than calling getPresignedViewUrl multiple times
 * @param {Array<string>} fileUrls - Array of S3 URLs or file keys
 * @param {string} organizationUuid - Organization UUID
 * @param {function} getAccessTokenSilently - Auth0 token function
 * @returns {Promise<Object>} - Map of original URL to presigned URL
 */
export const batchGetPresignedViewUrls = async (fileUrls, organizationUuid, getAccessTokenSilently) => {
  if (!fileUrls || fileUrls.length === 0) return {};

  const urlMap = {};

  try {
    // Fetch all presigned URLs in parallel
    const promises = fileUrls.map(async (fileUrl) => {
      const presignedUrl = await getPresignedViewUrl(fileUrl, organizationUuid, getAccessTokenSilently);
      urlMap[fileUrl] = presignedUrl || fileUrl; // Fallback to original if failed
    });

    await Promise.all(promises);
    return urlMap;
  } catch (error) {
    return urlMap;
  }
};
